import { useRef } from "react";

import { isEmpty } from "@s-libs/micro-dash";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { DirectoryType } from "@/admin/types";
import api from "@/api";
import buildSortParam from "@/directory/utils/buildSortParams/buildSortParams";
import { ClassroomInClassroomDirectory } from "@/types/models/ClassroomInClassroomDirectory.interface";
import { SchoolInSchoolDirectory } from "@/types/models/SchoolInSchoolDirectory.interface";
import { StudentInStudentDirectory } from "@/types/models/StudentInStudentDirectory.interface";
import { User } from "@/types/models/User/v3/User.interface";

interface ParamsToRetrieveDirectoryData {
  user: User | undefined | null;
  controls: Record<string, any> | undefined;
  directoryType: DirectoryType;
  defaultSort?: string;
  enabled?: boolean;
  pageSize?: number;
  params?: Record<string, any>;
  rawTableProps: Record<string, any> | undefined;
  searchParamName?: string;
  urlReplacers?: Record<string, any>;
  apiCatalogEndpoint: string;
  apiCatalogKey: string;
}

type DirectoryResult =
  | StudentInStudentDirectory[]
  | ClassroomInClassroomDirectory[]
  | SchoolInSchoolDirectory[];

type QueryResult = { count: number; results: DirectoryResult };

const useGetDirectoryQuery = ({
  user,
  controls = {},
  directoryType,
  defaultSort = "name",
  enabled = false,
  pageSize = 30,
  params = {},
  rawTableProps = {},
  searchParamName = "q",
  apiCatalogEndpoint,
  apiCatalogKey,
  urlReplacers,
}: ParamsToRetrieveDirectoryData): UseQueryResult<QueryResult, Error> => {
  const userProfileId = user?.pk;
  const userUniqueId = user?.unique_id;
  const currentSearch = useRef(null);
  const { setSortBy } = rawTableProps;
  const { pageIndex, globalFilter, sortBy } = controls;
  const { searchBoxFilterValue } = globalFilter || {};

  currentSearch.current = searchBoxFilterValue;
  const isDifferentSearch = currentSearch.current !== searchBoxFilterValue;

  const queryParams = {
    /** Filters out inactive users on Faculty and Classroom directory pages but does not work for the Student directory yet. There are plans to update the Student directory but until then we are filtering inactive users on the front end for Students.  */
    hide_archived: true,
    page: isEmpty(controls) ? 1 : pageIndex + 1,
    page_size: pageSize,
    sort:
      isEmpty(sortBy) || isDifferentSearch
        ? defaultSort
        : buildSortParam(sortBy),
    [searchParamName]: searchBoxFilterValue,
    ...params,
  };

  if (isDifferentSearch && rawTableProps) setSortBy([]);

  return useQuery({
    enabled,
    queryKey: [apiCatalogKey],
    queryFn: async () => {
      if (isEmpty(user)) {
        return;
      }

      const results = await api().get(apiCatalogEndpoint, {
        queryParams: {
          ...queryParams,
        },
        urlReplacers: [
          {
            directoryType,
            userUniqueId,
            id: userProfileId,
            ...urlReplacers,
          },
        ],
      });

      return results;
    },
  });
};

export default useGetDirectoryQuery;
